import { ChatSession, ChatMessage, ProviderConfig } from "../../core/types";
import {
  getProviders,
  getProvider,
  getSession,
  getOpenSessions,
  saveSession,
  getTruncatedHistory,
  isDebugMode,
  getStoryBible,
} from "../../core/storage";
import { sendMessage, isProviderApiError } from "../../core/adapters";
import { getPresetProviderName } from "../../core/providerPresets";
import { getStrings } from "../../core/i18n";
import { getSelectedText, isWordAvailable } from "../word";
import {
  escapeHtml,
  escapeAttr,
  renderMarkdownLite,
  toQuoteBlock,
  wireInfoIcons,
} from "./domUtils";
import {
  NOTE_DETECTION_INSTRUCTION,
  effectiveCritiquePrompt,
  regenerateInstruction,
  regenerateAcceptedApiContent,
  storyBibleSystemPromptPrefix,
} from "./chatPrompts";
import { renderSessionSwitcher, renderNewSessionSetup } from "./sessionSwitcher";

export interface ChatHandlers {
  onManageProviders: () => void;
  onOpenHistory: () => void;
  onManageStoryBibles: () => void;
  onOpenSettings: () => void;
}

// See storage.ts's isDebugMode for how this gets turned on (browser console only, no in-app
// toggle). Called right before every sendMessage() call (both a normal send and Regenerate) so
// it always logs the exact array actually being sent — including apiContent standing in for
// content wherever a message has one, and Regenerate's synthetic dissatisfaction-note turn,
// none of which is ever visible in the chat itself.
function logDebugRequest(
  config: { name: string; model: string; temperature?: number; systemPrompt?: string },
  messages: { role: string; content: string }[]
): void {
  if (!isDebugMode()) return;
  const temperatureLabel = config.temperature !== undefined ? `, temp ${config.temperature}` : "";
  console.log(
    `[NovelChat debug] Outgoing request → ${config.name} (${config.model}${temperatureLabel})`,
    // System prompt logged separately from `messages` — it's not itself a message, it's sent as
    // its own field on the request (see adapters/*.ts), so a log that only showed `messages`
    // silently omitted it entirely. This is also where a bound Story Bible's entries actually
    // show up (effectiveProviderConfig folds them in ahead of the plain system prompt — see
    // storyBibleSystemPromptPrefix), so this was a real gap: enabling debug mode couldn't
    // previously confirm a bible's content was actually reaching the model.
    { systemPrompt: config.systemPrompt, messages }
  );
}

// At most one DocumentSelectionChanged handler should ever be live at a time — always torn
// down before (re-)rendering, and only re-registered when an active session view is showing.
let activeSelectionHandlerCleanup: (() => void) | null = null;

function teardownSelectionHandler(): void {
  if (activeSelectionHandlerCleanup) {
    activeSelectionHandlerCleanup();
    activeSelectionHandlerCleanup = null;
  }
}

// Multi-window staleness polling (2026-08-11) — same "at most one live at a time, torn down
// before every (re-)render" pattern as activeSelectionHandlerCleanup above, for the same reason:
// renderActiveSession restarts this every time it runs, so without a teardown, switching chats
// or re-rendering repeatedly would stack up multiple intervals all polling the same or different
// sessions. This is the *background warning* half of the multi-window data-loss mitigation — a
// dismissible-by-reload banner for someone just reading a chat that's gone stale, who'd never
// otherwise notice since nothing they're doing triggers the save-time guard (sendUserMessage/
// deleteMessagePair/landRegeneratedReplyOntoFreshSession) that actually prevents data loss. See
// "Known gap, deliberately deferred: multi-window same-session data loss" in CLAUDE.md for the
// full two-piece design this is one half of.
let stalenessPollTimer: number | undefined;

function teardownStalenessPoll(): void {
  if (stalenessPollTimer !== undefined) {
    window.clearInterval(stalenessPollTimer);
    stalenessPollTimer = undefined;
  }
}

// Which session's chat (or the new-session picker) is showing below the tab strip. Remembered
// at module level so navigating away (e.g. to Manage Providers) and back doesn't lose your
// place, and so switching tabs doesn't require going through the router. Exported (as a type,
// plus the getContentMode/setContentMode pair below) so sessionSwitcher.ts's screens — which
// change this same state when you switch/close/start a chat — can read and write it without a
// module-level variable of their own; renderChat below still owns the variable itself.
// `providerId` on "new-session" (optional) pre-selects that provider in the picker's dropdown —
// set by providerList.ts's "Start new chat" button (via taskpane.ts's router) so picking a
// provider there and starting a chat with it is one click instead of add-provider-then-hunt-for-
// it-in-a-dropdown. Plain "+"/switching-to-new-session (sessionSwitcher.ts) leaves it unset,
// same as before this existed.
export type ContentMode =
  { kind: "session"; sessionId: string } | { kind: "new-session"; providerId?: string };

let contentMode: ContentMode | null = null;

export function getContentMode(): ContentMode | null {
  return contentMode;
}

export function setContentMode(mode: ContentMode | null): void {
  contentMode = mode;
}

// Whether the "chat exceeded context limit" banner (above the compose box) has been dismissed
// for the session it's currently tracking. Module-level for the same reason as contentMode
// above — renderChat() rebuilds the DOM from scratch after every send, so a variable scoped
// inside the render closure would forget the dismissal on the very next message. Explicit
// user request: dismissing should stick while you keep using *this* chat (including sending
// more messages in it), but come back once you leave and reopen it — so this only resets when
// renderActiveSession sees a *different* session id than last time, not on every re-render of
// the same one. Matches the existing convention that navigating to Providers/History and back
// isn't treated as "reopening" a chat either (contentMode itself survives that trip).
let truncationBannerDismissed = false;
let truncationBannerDismissedForSessionId: string | null = null;

// The one in-flight request per session, if any — keyed by session id, module-level so it
// survives whatever re-render triggered it being checked (Edit session, switching chats and
// back, etc. — same reasoning as truncationBannerDismissed above). Presence means "this session
// is busy"; `controller` is what the Send button (once it turns red/clickable) and the
// typing-bubble click both call .abort() on. `startedAt` lets a *fresh* render (e.g. after
// switching providers
// mid-flight) figure out how much of the STOP_THINKING_DELAY_MS window has already elapsed,
// rather than always restarting the 20s countdown from zero. Only ever holds one entry per
// session — sendUserMessage/regenerateReply both refuse to start a new request while their
// session already has one pending, so there's never ambiguity about what Stop cancels.
const pendingRequests = new Map<string, { controller: AbortController; startedAt: number }>();

function isSessionBusy(sessionId: string): boolean {
  return pendingRequests.has(sessionId);
}

// Accumulated reply text for a session's current in-flight streaming request, if any — set to
// "" the moment a request starts (sendUserMessage/regenerateReply) and appended to on every
// onChunk callback from the adapter. Module-level for the same reason pendingRequests is: it
// has to survive whatever re-render happens to run mid-stream (collapsing an unrelated reply,
// toggling EN/CH, switching chats and back), which wipes messageListEl's whole innerHTML — the
// render-restore check (see renderMessages' isSessionBusy branch) uses this to redraw the live
// bubble with its actual accumulated text instead of a blank spinner. Deleted at every exit
// path (landing, abort, error) in the same places pendingRequests itself is deleted, not later
// in a finally block — see the Request Lifecycle fix this mirrors: those blocks run *after* the
// synchronous renderChat() the landing helpers trigger, which is exactly the render this map
// needs to already be correct for.
const streamingReplyText = new Map<string, string>();

// How long the Send button stays as a plain, non-interactive grey "Thinking..." label before
// turning red and clickable (same text throughout — see applySendBtnPendingState) — per
// explicit request: no separate Stop button, and no way to cancel in the first moments of a
// request (most replies finish well before this and an immediately-clickable button reads as
// more alarming/premature than useful). 10s, revised down
// from an original 20s per follow-up feedback.
const STOP_THINKING_DELAY_MS = 10000;

// Whether the top toolbar (nav-cluster's Edit/Export/History/Providers row + the model/temp
// line) is collapsed to give the message list more vertical space. Module-level, same reason
// as contentMode above — persists across the full re-renders those rows go through (e.g.
// after sending a message, or switching chats via the session dropdown) rather than resetting
// every time. Defaults to collapsed (2026-08-05, per explicit feedback) so a freshly opened
// task pane favors message space over toolbar visibility from the start.
let topbarCollapsed = true;

// Which assistant replies are collapsed, keyed by `${sessionId}:${messageIndex}`. Module-level
// for the same reason as topbarCollapsed above: renderChat() fully rebuilds the DOM after every
// send, so this needs to live outside the render closure — otherwise every new reply would
// silently re-expand whatever the user had already collapsed. A flat Set keyed by session id is
// simpler than a Map of Sets and is fine here since messages are only ever appended, never
// reordered or removed (aside from whole-session delete, which just leaves orphaned keys that
// never get looked up again).
const collapsedMessageKeys = new Set<string>();

// Which sent-message quote blocks (the attached-selection passage in a user bubble) have been
// manually expanded, keyed the same way as collapsedMessageKeys. Opposite default from that
// one — a quote block starts collapsed (only collapsedMessageKeys' *absence* means "expand" for
// AI replies; here, presence in this set is what means "expanded"), so the default state for
// something never clicked is "not in the set" = collapsed, matching the request that these
// default to collapsed rather than expanded like AI replies do.
const expandedQuoteKeys = new Set<string>();

// Live-tracked, dismissible reference to whatever is currently selected in Word — mirrors how
// tools like GitHub Copilot Chat/Cursor attach the current selection as a removable chip rather
// than merging it as text you'd have to type/format yourself. Module-level, same reason as
// topbarCollapsed above: renderChat() does a full re-render after every AI reply (see the
// `renderChat(container, handlers)` call following a successful send below), and if these lived
// as local state inside renderActiveSession, that re-render would reset selectionIncluded back
// to true — silently re-attaching a selection the user had just excluded, even though nothing
// about the Word selection itself changed.
let pendingSelection: string | null = null;
// Whether the chip (if present) gets attached the next time you send. Defaults to true, and
// resets to true whenever the underlying Word selection actually changes to something new —
// click the chip to toggle it off without discarding it, so it's still there to turn back on
// for a later message rather than needing to re-highlight in Word.
let selectionIncluded = true;
// Which (session, exact passage) pairs "Critique this" has already been used on — greys out the
// button (renderChip) for a pair already in this set, so it can't be fired again for the same
// still-selected text in the same chat (nothing useful changes by critiquing the same passage
// twice there). Keyed by `${sessionId}:${text}`, not just the passage text — a bare
// text-only key (an earlier version of this) leaked across chats: switching to a *different*
// session that happened to have the same text selected would incorrectly show it as already
// critiqued, even though that chat never actually ran one. Storing the passage itself in the
// key (not just a boolean per session) is what makes the un-grey-out automatic within one
// chat: handleSelectionChanged already updates pendingSelection the moment the Word selection
// actually changes, so renderChip's own `.has(...)` check on the new key naturally goes false
// there — no separate reset needed. Module-level for the same reason as
// pendingSelection/selectionIncluded above: renderChat() fully rebuilds the DOM after every
// reply (including the critique's own), and local state inside renderActiveSession wouldn't
// survive that.
const critiqueUsedSelections = new Set<string>();

// Flat, non-fixed session list; any number of sessions can be open at once,
// including several with the same provider. Session titles stay at the default placeholder
// until explicitly renamed (pencil icon) — they don't auto-generate from the first message
// (tried, reverted per feedback: users didn't want the title changing out from under them).
export function renderChat(container: HTMLElement, handlers: ChatHandlers): void {
  const providers = getProviders();
  const openSessions = getOpenSessions();

  if (contentMode && contentMode.kind === "session") {
    const stillOpenId = contentMode.sessionId;
    if (!openSessions.some((s) => s.id === stillOpenId)) {
      contentMode = null;
    }
  }
  if (!contentMode) {
    // Default to the most-recently-updated session that still has a usable provider — skips
    // over orphaned sessions (provider deleted) rather than getting stuck showing one.
    const defaultSession = openSessions.find((s) => providers.some((p) => p.id === s.providerId));
    if (!defaultSession) {
      // No open chat to land on (every chat closed/deleted, or every one left is orphaned) —
      // go to the provider list instead of silently defaulting to "Start a new chat", 2026-08-11
      // per explicit request. Only reachable with providers.length > 0: taskpane.ts's router
      // sends a providers.length === 0 "auto" straight to onboarding, before renderChat ever
      // runs. Explicit paths into "Start a new chat" (the switcher's "+", providerList.ts's
      // "Start new chat" button) both already set contentMode before calling renderChat, so
      // they never reach this branch at all — this only replaces the *implicit* fallback.
      handlers.onManageProviders();
      return;
    }
    contentMode = { kind: "session", sessionId: defaultSession.id };
  }
  // A mutable module-level `let` doesn't stay narrowed inside nested closures (event handlers,
  // .map() callbacks below) — capture the resolved value once so the rest of this render pass
  // works with a properly-typed local instead.
  const mode = contentMode;
  const activeSessionId = mode.kind === "session" ? mode.sessionId : null;

  teardownSelectionHandler();
  teardownStalenessPoll();

  container.innerHTML = `
    <div class="screen chat-screen">
      <div class="session-switcher${topbarCollapsed ? " is-collapsed" : ""}" id="session-switcher"></div>
      <div id="chat-content"></div>
    </div>
  `;

  const switcherEl = container.querySelector("#session-switcher") as HTMLElement;
  const contentEl = container.querySelector("#chat-content") as HTMLElement;

  renderSessionSwitcher(switcherEl, providers, openSessions, activeSessionId, handlers, container);

  if (mode.kind === "new-session") {
    renderNewSessionSetup(contentEl, providers, openSessions, handlers, container, mode.providerId);
  } else {
    const session = openSessions.find((s) => s.id === mode.sessionId);
    if (session) {
      renderActiveSession(contentEl, session, handlers, container);
    }
  }
}

// Resolves one of the three tri-state override fields (temperature, maxContextTokens — see
// ChatSession's own comments on each) against its provider-level counterpart: `null` ("clear")
// bypasses the provider value entirely and resolves to `undefined`, so the downstream consumer
// (the adapter for temperature, getTruncatedHistory for maxContextTokens) applies *its own*
// "nothing was given" fallback — which is already exactly "the model's own default" in both
// cases, so no separate model-default computation is needed here. `undefined` ("inherit") falls
// through to the provider's value via `??` as before; a real value ("override") is used as-is.
function resolveSessionOverride<T>(
  sessionValue: T | null | undefined,
  providerValue: T | undefined
): T | undefined {
  if (sessionValue === null) return undefined;
  return sessionValue ?? providerValue;
}

// The temperature actually used when sending: the session's own override if set, else the
// provider's. Shared by the model-tag display and the merged config passed to sendMessage below,
// so the number shown in the header is always the number that's really sent.
function effectiveTemperature(session: ChatSession, provider: ProviderConfig): number | undefined {
  return resolveSessionOverride(session.temperature, provider.temperature);
}

function formatSessionTag(session: ChatSession, provider: ProviderConfig): string {
  const s = getStrings();
  const temperature = effectiveTemperature(session, provider);
  return s.chat.sessionTag(
    getPresetProviderName(provider),
    session.model,
    temperature !== undefined ? String(temperature) : s.chat.defaultValue
  );
}

// The config actually used when sending or budgeting for this session: the provider's own
// settings, with model/temperature/systemPrompt/maxContextTokens/maxOutputTokens each replaced
// by the session's own override (Edit session) where one is set. Shared by every call site that
// needs the *real* effective settings (getTruncatedHistory's budget math, the actual sendMessage
// calls in sendUserMessage/regenerateReply) so this merge happens in exactly one place — model/
// temperature already needed it inline before session-level overrides existed for the other
// three; this generalizes that instead of copying the same spread three times. Also fixes a
// latent inconsistency: getTruncatedHistory used to be called with the raw `provider`, whose
// `.model` is only that provider's *default* model — a session using a different model (a very
// normal case, Edit session's whole point) was silently budgeting context against the wrong
// model's size estimate. Passing this merged object through instead means the budget always
// matches what's actually about to be sent.
function effectiveProviderConfig(session: ChatSession, provider: ProviderConfig): ProviderConfig {
  const systemPrompt = session.systemPrompt ?? provider.systemPrompt;
  // Story Bible facts are a separate instruction layer from the system prompt itself (see
  // storyBibleSystemPromptPrefix's own comment) — folded on ahead of it here, at the same single
  // merge point everything else in this function already goes through, rather than touching the
  // tri-state inherit/override/clear system prompt machinery. Only entries the author left
  // switched on (StoryBibleEntry.included) count, so a bible can grow past what's actually worth
  // sending without deleting anything.
  const bible = session.storyBibleId ? getStoryBible(session.storyBibleId) : undefined;
  const includedEntries = (bible?.entries ?? []).filter((e) => e.included && e.text.trim());
  const effectiveSystemPrompt =
    includedEntries.length > 0
      ? [storyBibleSystemPromptPrefix(includedEntries), systemPrompt].filter(Boolean).join("\n\n")
      : systemPrompt;
  return {
    ...provider,
    model: session.model,
    temperature: effectiveTemperature(session, provider),
    systemPrompt: effectiveSystemPrompt,
    maxContextTokens: resolveSessionOverride(session.maxContextTokens, provider.maxContextTokens),
    maxOutputTokens: session.maxOutputTokens ?? provider.maxOutputTokens,
  };
}

function renderActiveSession(
  contentEl: HTMLElement,
  session: ChatSession,
  handlers: ChatHandlers,
  container: HTMLElement
): void {
  const s = getStrings();
  const provider = getProvider(session.providerId);

  if (truncationBannerDismissedForSessionId !== session.id) {
    truncationBannerDismissed = false;
    truncationBannerDismissedForSessionId = session.id;
  }

  // One-time backfill for sessions saved before ChatSession.totalTokensUsed existed: seeds it
  // (and the input/output split fields alongside it, same trigger — see ChatSession.
  // totalInputTokensUsed/totalOutputTokensUsed) from whatever usage is already on this session's
  // messages, persisted immediately (not just computed for display) so that deleting a message
  // pair before ever sending a new one still can't quietly drop that history's already-billed
  // tokens out of the running total.
  if (session.totalTokensUsed === undefined) {
    const seedIn = session.messages.reduce(
      (sum, m) => sum + (m.usage ? m.usage.inputTokens : 0),
      0
    );
    const seedOut = session.messages.reduce(
      (sum, m) => sum + (m.usage ? m.usage.outputTokens : 0),
      0
    );
    if (seedIn + seedOut > 0) {
      session.totalTokensUsed = seedIn + seedOut;
      session.totalInputTokensUsed = seedIn;
      session.totalOutputTokensUsed = seedOut;
      saveSession(session);
    }
  }

  contentEl.innerHTML = `
    <div class="topbar-toggle-row">
      <div class="topbar-toggle-row-main">
        <button
          type="button"
          class="topbar-toggle-btn"
          id="topbar-toggle-btn"
          title="${topbarCollapsed ? s.chat.showToolbar : s.chat.hideToolbar}"
          aria-label="${topbarCollapsed ? s.chat.showToolbar : s.chat.hideToolbar}"
          aria-expanded="${topbarCollapsed ? "false" : "true"}"
        ><svg class="topbar-toggle-icon${topbarCollapsed ? "" : " is-up"}" width="10" height="10" viewBox="0 0 12 12" aria-hidden="true"><path d="M2 4.5l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg></button>
        <span class="model-tag" title="${provider ? escapeAttr(formatSessionTag(session, provider)) : ""}">${
          provider ? escapeHtml(formatSessionTag(session, provider)) : s.history.unknownProvider
        }</span>
      </div>
      <div class="session-token-total" id="session-token-total" style="display:none;">
        <span class="session-token-total-label">${s.chat.totalTokensLabel}</span>
        <span id="session-token-total-value"></span>
        <span class="info-icon-wrap" id="session-token-info">
          <button type="button" class="info-icon" aria-label="${s.chat.tokenUsageAria}">$</button>
          <div class="info-bubble" id="session-token-info-bubble"></div>
        </span>
      </div>
    </div>
    <div class="message-list" id="message-list"></div>
    <div id="truncation-banner" class="truncation-banner" style="display:none;">
      <span class="truncation-banner-text">${s.chat.contextExceededBanner}</span>
      <button type="button" class="truncation-banner-close" aria-label="${s.chat.dismissAria}">&times;</button>
    </div>
    <div id="staleness-banner" class="staleness-banner" style="display:none;">
      <span class="staleness-banner-text">${s.chat.staleChatBanner}</span>
      <button type="button" id="staleness-banner-reload" class="primary-btn staleness-banner-reload">${s.chat.reload}</button>
    </div>
    <p id="chat-error" class="error-text" style="display:none;"></p>
    <div id="selection-chip-container"></div>
    <div id="regenerate-banner" class="regenerate-banner" style="display:none;">
      <span class="regenerate-banner-text">${s.chat.regenerateBanner}</span>
      <button type="button" class="regenerate-banner-cancel" aria-label="${s.chat.cancelRegenerateAria}">&times;</button>
    </div>
    <form id="chat-input-form" class="chat-input-form">
      <textarea id="chat-input" rows="3" placeholder="${escapeAttr(s.chat.typeMessagePlaceholder)}"></textarea>
      <div class="send-btn-column">
        <button type="submit" id="send-btn" class="primary-btn">${s.chat.send}</button>
      </div>
    </form>
  `;

  const errorEl = contentEl.querySelector("#chat-error") as HTMLElement;
  const truncationBannerEl = contentEl.querySelector("#truncation-banner") as HTMLElement;
  truncationBannerEl.querySelector(".truncation-banner-close")?.addEventListener("click", () => {
    truncationBannerDismissed = true;
    truncationBannerEl.style.display = "none";
  });

  // The background half of the multi-window staleness mitigation — see
  // teardownStalenessPoll's own comment above for the full picture. Checks every 5s whether
  // storage's `updatedAt` for this session has moved past what this render still has, which
  // would mean another window changed it since this one last loaded/rendered it. Reload just
  // re-runs renderChat() — contentMode still points at this same session, so it naturally
  // re-fetches and re-renders current data, same as switching away and back would.
  const stalenessBannerEl = contentEl.querySelector("#staleness-banner") as HTMLElement;
  stalenessBannerEl.querySelector("#staleness-banner-reload")?.addEventListener("click", () => {
    renderChat(container, handlers);
  });
  stalenessPollTimer = window.setInterval(() => {
    // This screen may no longer be on top (navigated to Providers/History and back never tore
    // this down — same gap teardownSelectionHandler's own DocumentSelectionChanged listener
    // already has) — self-terminate rather than poll forever against a detached banner.
    if (!stalenessBannerEl.isConnected) {
      teardownStalenessPoll();
      return;
    }
    const fresh = getSession(session.id);
    stalenessBannerEl.style.display = fresh && fresh.updatedAt !== session.updatedAt ? "" : "none";
  }, 5000);

  const sessionTokenTotalEl = contentEl.querySelector("#session-token-total") as HTMLElement;
  const sessionTokenTotalValueEl = contentEl.querySelector(
    "#session-token-total-value"
  ) as HTMLElement;
  const sessionTokenInfoBubbleEl = contentEl.querySelector(
    "#session-token-info-bubble"
  ) as HTMLElement;
  // Wired once here (this element itself is part of the static per-session template, never
  // rebuilt by renderMessages) — only its content and show/hide state change on each render,
  // same reasoning as truncationBannerEl above.
  wireInfoIcons(contentEl);

  function showError(message: string): void {
    errorEl.textContent = message;
    errorEl.style.display = "";
  }

  const messageListEl = contentEl.querySelector("#message-list") as HTMLElement;
  const chipContainer = contentEl.querySelector("#selection-chip-container") as HTMLElement;
  const regenerateBannerEl = contentEl.querySelector("#regenerate-banner") as HTMLElement;
  const form = contentEl.querySelector("#chat-input-form") as HTMLFormElement;
  const input = contentEl.querySelector("#chat-input") as HTMLTextAreaElement;
  const sendBtn = contentEl.querySelector("#send-btn") as HTMLButtonElement;

  // Which reply Regenerate is currently targeting, from the moment its per-message button is
  // clicked until the resulting request actually resolves (success/cancel/error) — not just
  // while the compose box is waiting for optional feedback. Local to this render closure (not
  // module-level like collapsedMessageKeys etc.) because it doesn't need to survive a full
  // renderChat(): the success path replaces this whole closure anyway, and the only other
  // callers that touch DOM outside it (cancel/error paths) already run inside this same
  // closure. renderMessages() reads this to highlight the target message (.is-regenerate-target)
  // so the highlight survives incidental renderMessages() calls (e.g. collapsing an unrelated
  // reply) without extra bookkeeping.
  let pendingRegenerateIndex: number | null = null;

  regenerateBannerEl
    .querySelector(".regenerate-banner-cancel")
    ?.addEventListener("click", () => cancelPendingRegenerate());

  contentEl.querySelector("#topbar-toggle-btn")?.addEventListener("click", () => {
    topbarCollapsed = !topbarCollapsed;
    renderChat(container, handlers);
  });

  function stopPendingRequest(): void {
    pendingRequests.get(session.id)?.controller.abort();
  }
  // Only ever does something once sendBtn has flipped to type="button" (see
  // applySendBtnPendingState below) — while it's still type="submit" (the first
  // STOP_THINKING_DELAY_MS of waiting), a click here is the normal Send action, handled by the
  // form's own "submit" listener further down, not this one.
  sendBtn.addEventListener("click", () => {
    if (sendBtn.type === "button") stopPendingRequest();
  });

  // Three visual states on the one button (no separate Stop button), per explicit request —
  // text stays "Thinking..." throughout both pending states (an earlier version changed it to
  // "Stop Thinking" once clickable; reverted per feedback in favor of just a color/weight
  // change, simpler and doesn't risk resizing the button with different wording). idle "Send"
  // (blue, .primary-btn's own default, no extra classes) → grey "Thinking..." (`.is-thinking`,
  // disabled, not interactive — the first STOP_THINKING_DELAY_MS of waiting) → red bold
  // "Thinking..." (`.is-thinking.is-stop-thinking` layered on top, enabled/clickable) once that
  // elapses. `#send-btn`'s own `min-width` (taskpane.css) is what actually guarantees the
  // button doesn't resize on this last transition — same text either way, but bold glyphs are a
  // few px wider than normal ones at the same font-size, which would otherwise still shift the
  // button even with identical wording. `elapsedMs` lets this be called correctly both when a
  // request is actually starting (elapsedMs: 0) and from a *fresh* render picking up a request
  // that's been running since an earlier render's closure (elapsedMs: however long it's
  // actually been) — see the isSessionBusy block below. The scheduled callback re-checks
  // pendingRequests before touching anything, so it's a safe no-op if the request already
  // finished (or was superseded) by the time it fires, rather than clobbering an idle button
  // back into the clickable red state.
  function applySendBtnPendingState(
    elapsedMs: number,
    sessionId: string,
    controller: AbortController
  ): void {
    input.disabled = true;
    sendBtn.classList.add("is-thinking");
    sendBtn.textContent = s.chat.thinking;
    if (elapsedMs >= STOP_THINKING_DELAY_MS) {
      sendBtn.type = "button";
      sendBtn.disabled = false;
      sendBtn.classList.add("is-stop-thinking");
      return;
    }
    sendBtn.type = "submit";
    sendBtn.disabled = true;
    sendBtn.classList.remove("is-stop-thinking");
    setTimeout(() => {
      const current = pendingRequests.get(sessionId);
      if (!current || current.controller !== controller) return;
      sendBtn.type = "button";
      sendBtn.disabled = false;
      sendBtn.classList.add("is-stop-thinking");
    }, STOP_THINKING_DELAY_MS - elapsedMs);
  }

  // This is a *fresh* render — its own input/sendBtn start out enabled by default, with no
  // memory of a request that started in an *earlier* render's closure and is still running
  // (e.g. after switching providers mid-flight, or navigating away and back to this same chat
  // while it's busy). pendingRequests is what actually tracks that across renders, so
  // re-applying the busy Send-button UI here — right after building this render's DOM, before
  // anything else touches it — is what makes it actually persist visually, not just internally.
  // The typing indicator's own restoration is handled inside renderMessages() below instead of
  // here — this used to also call showTypingIndicator() directly at this point, but the first
  // renderMessages(true) call further down (the initial render of the message list) unconditionally
  // rebuilds messageListEl.innerHTML, which silently wiped out whatever was just appended here.
  // Confirmed real, reported directly by the user: switching to a different chat and back while a
  // reply was pending made the typing bubble disappear every time.
  const pending = pendingRequests.get(session.id);
  if (pending) {
    applySendBtnPendingState(Date.now() - pending.startedAt, session.id, pending.controller);
  }

  // Message history and write-back (Insert/Replace) don't depend on the provider still
  // existing — only *sending a new message* does. Rendered unconditionally below, so a session
  // whose provider was deleted still shows its full history and can still write old replies
  // into the document; only the "provider is gone" branch further down disables the input.
  // scrollToBottom defaults to false: most calls are an in-place update (toggling a collapse
  // arrow, deleting a pair) where the user's current scroll position should stay put — jumping
  // to the bottom on every click was the actual bug being fixed here. The few calls that really
  // do want to reveal the latest message (opening a session, having just sent one) pass true
  // explicitly.
  // Marks exactly where getTruncatedHistory's cutoff falls in the visible list — replaces an
  // earlier version of this feature that only showed a single yes/no banner below the whole
  // list ("some history isn't being sent"), with no indication of where. provider may be
  // undefined here (session's provider was deleted) — getTruncatedHistory falls back to a safe
  // default budget in that case, same as every other call site. Recomputed on every
  // renderMessages() call rather than cached, since the cutoff genuinely can move (a provider's
  // context budget or model can change between renders). Also returns usedTokens — the real
  // current size of this chat's context (everything that would actually be resent right now,
  // plus system prompt) — reused below for the "$" popup's Context window row, so both the
  // divider and that row are always computed from the exact same truncation pass rather than two
  // separately-drifting estimates.
  function computeTruncationInfo(): { dropped: number; usedTokens: number } {
    const { messages: included, usedTokens } = getTruncatedHistory(
      session,
      provider ? effectiveProviderConfig(session, provider) : undefined
    );
    return { dropped: session.messages.length - included.length, usedTokens };
  }

  function renderMessages(scrollToBottom = false): void {
    const previousScrollTop = messageListEl.scrollTop;
    const { dropped, usedTokens: contextWindowTokens } = computeTruncationInfo();
    // A single divider at the exact cutoff, not a marker on every dropped message — a long chat
    // could have dozens of dropped messages, so decorating each one gets noisy fast, and one
    // clear boundary says the same thing. The two flanking lines use CSS's absolute "cm" unit
    // directly (not a hand-converted px value) so "at least 1cm of red line at both ends" holds
    // regardless of screen DPI; the label between them shrinks and ellipsis-truncates first
    // under narrow widths, via min-width: 0 overriding flex's default auto (which would
    // otherwise refuse to shrink below the text's natural width).
    const truncationDividerHtml =
      dropped > 0
        ? `<div class="truncation-divider" title="${s.chat.contextExceededDivider}">
             <span class="truncation-divider-line"></span>
             <span class="truncation-divider-text">${s.chat.contextExceededDivider}</span>
             <span class="truncation-divider-line"></span>
           </div>`
        : "";
    // Same line/label/line shape as the truncation divider above, in the app's blue (matching
    // Send/.primary-btn) instead of red — a different kind of marker (an event that happened at
    // a point in time), not a warning, so it deliberately doesn't borrow the red "needs
    // attention" treatment. One divider per logged change (see ChatSession.providerChanges),
    // positioned by comparing each message's own timestamp against changedAt rather than a
    // stored array index — see that field's comment for why (survives message deletion with no
    // extra bookkeeping). findIndex returning -1 (the change happened after every message
    // currently in the array — the normal case, right after switching) falls through to
    // session.messages.length, placing the divider at the very end.
    const providerChangeSplices = (session.providerChanges ?? []).map((change) => {
      const index = session.messages.findIndex((m) => m.timestamp >= change.changedAt);
      const html = `<div class="provider-change-divider" title="${escapeAttr(s.chat.providerChangedDivider(change.providerName))}">
             <span class="provider-change-divider-line"></span>
             <span class="provider-change-divider-text">${escapeHtml(s.chat.providerChangedDivider(change.providerName))}</span>
             <span class="provider-change-divider-line"></span>
           </div>`;
      return { index: index === -1 ? session.messages.length : index, html };
    });
    const messageEls = session.messages.map((m: ChatMessage, index: number) => {
      const isAssistant = m.role === "assistant";
      const isCollapsed = isAssistant && collapsedMessageKeys.has(`${session.id}:${index}`);

      // "Response time" (how long the reply took to arrive — chat.ts's sendUserMessage/
      // regenerateReply, ChatMessage.responseTimeSeconds) sits on its own line ahead of the
      // token count, per explicit request. Only present on messages that actually landed a
      // real reply — same condition as `usage` itself.
      let tokenLabel = "";
      if (isAssistant && m.usage) {
        const responseTimeLabel =
          m.responseTimeSeconds !== undefined
            ? `${s.chat.responseTime(String(m.responseTimeSeconds))}<br>`
            : "";
        tokenLabel = `${responseTimeLabel}${s.chat.tokensInOut(m.usage.inputTokens.toLocaleString(), m.usage.outputTokens.toLocaleString())}`;
      }

      // Bottom-left arrow + delete (X), bottom-right token count, same row — same chevron
      // shape/rotation convention as the topbar-toggle-btn above (up = "click to collapse",
      // down = "click to expand"), just scoped to one reply instead of the whole toolbar.
      // The X's confirmation is a floating popup (.delete-confirm-popup), not a row swap — same
      // pattern as providerForm.ts's "?" info icons (position:relative wrap + absolutely
      // positioned bubble, toggled open/closed via a class, closed by an outside click). That
      // way the row itself (arrow, token count) never changes; only the popup appears on top.
      const footer = isAssistant
        ? `<div class="message-footer">
               <div class="message-footer-left">
                 <button
                   type="button"
                   class="bubble-collapse-btn"
                   data-action="toggle-collapse"
                   data-index="${index}"
                   title="${isCollapsed ? s.chat.expandReply : s.chat.collapseReply}"
                   aria-label="${isCollapsed ? s.chat.expandReply : s.chat.collapseReply}"
                   aria-expanded="${isCollapsed ? "false" : "true"}"
                 ><svg class="bubble-collapse-icon${isCollapsed ? "" : " is-up"}" width="10" height="10" viewBox="0 0 12 12" aria-hidden="true"><path d="M2 4.5l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg></button>
                 <span class="delete-confirm-wrap">
                   <button
                     type="button"
                     class="bubble-delete-btn"
                     data-action="toggle-delete-confirm"
                     title="${s.chat.deleteExchangeTitle}"
                     aria-label="${s.chat.deleteReplyConfirmAria}"
                   >&times;</button>
                   <div class="delete-confirm-popup">
                     <p>${s.chat.deleteReplyConfirmText}</p>
                     <div class="delete-confirm-popup-actions">
                       <button type="button" class="danger-btn" data-action="confirm-delete-pair" data-index="${index}">${s.common.yesDelete}</button>
                       <button type="button" class="secondary-btn" data-action="cancel-delete-pair">${s.common.cancel}</button>
                     </div>
                   </div>
                 </span>
               </div>
               ${tokenLabel ? `<span class="message-tokens">${tokenLabel}</span>` : ""}
             </div>`
        : "";

      // Collapsed leaves only the footer row (arrow + delete + token count) — no reply text and
      // no Regenerate action, since there's nothing left to react to while collapsed. Only
      // shows when there's a working provider to actually call (mirrors the "provider is gone"
      // gating on the input box below). Also hidden for a critique reply (2026-08-12) — the
      // rubric that told the AI what to do is deliberately gone by the time any regenerate could
      // run (see ChatMessage.apiContentKind), so regenerating here would silently ask for "a
      // different take" with no task definition left to redo it against. Simpler to not offer it
      // than to keep the rubric alive indefinitely just for this one edge case.
      //
      // Restricted to the *last* message only, 2026-08-12 (was: every reply, any time) — fixes a
      // real bug, not just a simplification: pendingRegenerateIndex (below) is captured once,
      // when Regenerate is clicked, and stayed trusted until submit. Deleting an *older* message
      // pair while a regenerate was pending shifted every later index, so submitting could
      // silently regenerate whatever reply now sat at that stale number — not the one actually
      // clicked. Restricting to "last reply" means deleteMessagePair (which now cancels any
      // pending regenerate outright, see its own comment) is the only thing that could ever
      // invalidate the target, and it already closes that gap directly.
      const isCritiqueReply = session.messages[index - 1]?.apiContentKind === "critique";
      const isLastMessage = index === session.messages.length - 1;
      const actions =
        isAssistant && !isCollapsed && provider && !isCritiqueReply && isLastMessage
          ? `<div class="message-actions">
                 <button type="button" class="secondary-btn" data-action="regenerate" data-index="${index}">${s.chat.regenerateLabel}</button>
               </div>`
          : "";
      // The quote block inside a *sent* message (the attached-selection passage — see
      // sendCurrentMessage) gets its own collapse arrow, defaulting to collapsed so a long
      // selection doesn't dominate the chat history. Only wired up for user messages;
      // renderMarkdownLite no-ops this option for text with no "> " lines in it anyway.
      const quoteKey = `${session.id}:${index}`;
      // Always rendered in full, even when isCollapsed — the .message-bubble-text wrapper is
      // what actually truncates to a fixed 2 lines (CSS line-clamp, see .is-collapsed below),
      // same technique as the quote block above, rather than blanking the content entirely.
      const bubbleContent = `<div class="message-bubble-text">${renderMarkdownLite(
        m.content,
        !isAssistant ? { key: quoteKey, isExpanded: expandedQuoteKeys.has(quoteKey) } : undefined
      )}</div>`;

      // .is-regenerate-target highlights whichever reply Regenerate currently targets — set the
      // moment its button is clicked and cleared only once the resulting request resolves (see
      // pendingRegenerateIndex above), so the highlight stays visible through the actual
      // in-flight request, not just the "waiting for optional feedback" moment before submit.
      const isRegenerateTarget = isAssistant && index === pendingRegenerateIndex;
      return `
      <div class="message message-${m.role}${isRegenerateTarget ? " is-regenerate-target" : ""}" data-message-index="${index}">
        <div class="message-bubble${isCollapsed ? " is-collapsed" : ""}">${bubbleContent}${footer}</div>
        ${actions}
      </div>`;
    });
    // All splice points combined and applied highest-index-first, so inserting one never shifts
    // the target position of another still waiting to be inserted. The truncation divider is
    // spliced right before the first message that's actually still sent — everything before that
    // point in the array is the dropped, oldest portion.
    const allSplices = [...providerChangeSplices];
    if (dropped > 0) {
      allSplices.push({ index: dropped, html: truncationDividerHtml });
    }
    allSplices
      .sort((a, b) => b.index - a.index)
      .forEach(({ index, html }) => {
        messageEls.splice(index, 0, html);
      });
    messageListEl.innerHTML = messageEls.join("");
    messageListEl.scrollTop = scrollToBottom ? messageListEl.scrollHeight : previousScrollTop;
    // The typing indicator lives outside session.messages (see showTypingIndicator's own
    // comment — it's DOM-only, never persisted), so the innerHTML rebuild just above always
    // wipes it out with everything else. Re-added here, driven by pendingRequests (the same
    // source of truth the Send button's pending state already uses across renders), so it
    // survives every reason renderMessages() gets called while a reply is still in flight — not
    // just the initial render, but also switching to a different chat and back, or collapsing/
    // deleting/toggling a different message in the same chat while waiting. Previously only
    // shown via a one-off showTypingIndicator() call at the moment a request started, which is
    // exactly why it silently vanished on any of those other triggers. showTypingIndicator()
    // itself now also restores whatever's already accumulated in streamingReplyText for this
    // session — so a mid-stream rebuild redraws the reply-so-far, not a blank spinner.
    if (isSessionBusy(session.id)) {
      showTypingIndicator();
    }
    truncationBannerEl.style.display = dropped > 0 && !truncationBannerDismissed ? "" : "none";
    wireCollapseButtons();
    wireDeleteButtons();
    wireRegenerateButtons();
    wireQuoteCollapseButtons();
    const totalIn = getSessionTotalInputTokens();
    const totalOut = getSessionTotalOutputTokens();
    const totalCombined = totalIn + totalOut;
    if (totalCombined > 0) {
      sessionTokenTotalEl.style.display = "";
      sessionTokenTotalValueEl.textContent = totalCombined.toLocaleString();
      // Deliberately doesn't call this an "estimate" or "approximate" figure — these are the
      // real numbers each provider's own API response reported (ChatMessage.usage), not
      // something NovelChat calculates itself. What NovelChat genuinely can't do is turn this
      // into a dollar cost (no pricing data, and input/output are usually billed at different
      // per-token rates) — hence pointing at the provider's own pricing/account info for that,
      // rather than attempting a cost estimate here that could be wrong.
      // Wording history: three rounds of revision on 2026-08-10, all per explicit feedback.
      // Round 1 dropped "usage dashboard" jargon and an implementation-detail line that was
      // never meant to be user-facing. Round 2 rewrote it for a general-public audience, not
      // one specific familiar user — NovelChat is intended for eventual public release.
      // Round 3 restructured a single prose disclaimer
      // sentence into labeled metric rows plus a separate one-line explanation of what a token
      // even is, matching how enterprise usage/billing panels (AWS, Azure, Stripe) structure
      // this. Round 4: position reverted from the topbar back to above Send (per explicit
      // "I dont like the position" feedback), icon changed from "?" to "$" (still the same
      // circle/shade as every other .info-icon-wrap use — see .info-icon), and the plain
      // "Total Tokens: <number>" text brought back next to the icon — only the icon itself is
      // interactive, the label/number are plain siblings outside .info-icon-wrap. Round 5, this
      // one: explicitly states costs aren't charged by NovelChat itself — "billed by your AI
      // provider" alone implied but never ruled that out, a real gap for a stranger encountering
      // a "$" icon on first use, now that public release is the goal (not just the app's
      // original day-to-day user, who already knows this is BYOK/zero-backend).
      //
      // Total tokens/Context window rows added 2026-08-13, per explicit request. Total tokens is
      // just totalCombined restated as its own row (the same lifetime figure already shown next
      // to the icon). Context window is the current chat's real size right now — not a fixed
      // ceiling — per explicit correction: it's everything that would actually be resent on the
      // next message (the truncation-surviving history plus system prompt), so it grows as the
      // conversation continues and can shrink (deleting a message pair, or a provider/session
      // change that now truncates more away). Computed once, above, by computeTruncationInfo()
      // alongside the truncation-divider math — same getTruncatedHistory call, not a second
      // estimate that could drift from what the divider shows.
      sessionTokenInfoBubbleEl.innerHTML =
        `<strong>${s.chat.tokenUsageHeading}</strong><br>` +
        `${s.chat.inputTokensLabel} ${totalIn.toLocaleString()}<br>` +
        `${s.chat.outputTokensLabel} ${totalOut.toLocaleString()}<br>` +
        `${s.chat.totalTokensRowLabel} ${totalCombined.toLocaleString()}<br>` +
        `${s.chat.contextWindowLabel} ${contextWindowTokens.toLocaleString()}<br><br>` +
        s.chat.tokenUsageInfo;
    } else {
      sessionTokenTotalEl.style.display = "none";
    }
  }

  // Reads the session's persisted running total (see ChatSession.totalInputTokensUsed/
  // totalOutputTokensUsed) rather than summing session.messages — that sum would drop every
  // time a message pair is deleted, even though the tokens were already sent to and billed by
  // the provider before the delete. Falls back to summing current messages only for sessions
  // saved before these fields existed; that fallback is a read-only stand-in for display, not a
  // persist — see applyUsageToFreshSession below, which seeds these fields from this same sum
  // (via the same fallback logic, applied to a freshly-fetched session rather than this
  // closure's own) the first time such a session receives a new reply, so it only ever needs to
  // happen once per session. Split by direction for the topbar token-usage popup, which needs
  // input and output separate since providers bill them at different per-token rates (a
  // combined number can't be turned into a cost estimate).
  function getSessionTotalInputTokens(): number {
    if (session.totalInputTokensUsed !== undefined) return session.totalInputTokensUsed;
    return session.messages.reduce((sum, m) => sum + (m.usage ? m.usage.inputTokens : 0), 0);
  }

  function getSessionTotalOutputTokens(): number {
    if (session.totalOutputTokensUsed !== undefined) return session.totalOutputTokensUsed;
    return session.messages.reduce((sum, m) => sum + (m.usage ? m.usage.outputTokens : 0), 0);
  }

  // Shared by both landing helpers below — bumps a fresh session's token totals by exactly
  // this one message's usage, using the same fallback-to-summing-messages logic as
  // getSessionTotalInputTokens/OutputTokens above, just applied to `fresh` instead of the
  // closure's own `session`.
  function applyUsageToFreshSession(fresh: ChatSession, usage: ChatMessage["usage"]): void {
    const inputTokens = usage?.inputTokens ?? 0;
    const outputTokens = usage?.outputTokens ?? 0;
    const priorTotal =
      fresh.totalTokensUsed ??
      fresh.messages.reduce(
        (sum, m) => sum + (m.usage ? m.usage.inputTokens + m.usage.outputTokens : 0),
        0
      );
    const priorIn =
      fresh.totalInputTokensUsed ??
      fresh.messages.reduce((sum, m) => sum + (m.usage ? m.usage.inputTokens : 0), 0);
    const priorOut =
      fresh.totalOutputTokensUsed ??
      fresh.messages.reduce((sum, m) => sum + (m.usage ? m.usage.outputTokens : 0), 0);
    fresh.totalTokensUsed = priorTotal + inputTokens + outputTokens;
    fresh.totalInputTokensUsed = priorIn + inputTokens;
    fresh.totalOutputTokensUsed = priorOut + outputTokens;
  }

  function renderIfStillOnScreen(sessionId: string): void {
    if (
      container.querySelector(".chat-screen") &&
      contentMode?.kind === "session" &&
      contentMode.sessionId === sessionId
    ) {
      renderChat(container, handlers);
    }
  }

  // The core of the race-condition fix: appends a just-arrived reply onto the *current* persisted
  // session — fetched fresh from storage, not the `session` object this closure has been
  // holding since before the request started. That stale object is exactly what caused the
  // original bug (switching providers mid-reply, then the late reply's save silently
  // overwriting the newer data with the stale snapshot). Safe to always append at the end
  // rather than find a specific insertion point, because sendUserMessage/regenerateReply both
  // refuse to start a new request while this session already has one pending (isSessionBusy) —
  // so nothing else can have added or removed messages in the meantime. Returns without doing
  // anything if the session was deleted (via History) while the request was in flight —
  // saveSession() upserts by id, so saving here would silently resurrect it.
  function landReplyOntoFreshSession(assistantMessage: ChatMessage): void {
    // The stream (if any) has genuinely ended by the time this runs, one way or another — clear
    // its accumulated-text entry unconditionally, up front, same "before the render, not left to
    // a finally block" reasoning as pendingRequests.delete below. Left stale here, a render
    // triggered some other way afterward could redraw a bubble showing already-landed text as
    // if it were still streaming.
    streamingReplyText.delete(session.id);
    const fresh = getSession(session.id);
    if (!fresh) return;
    applyUsageToFreshSession(fresh, assistantMessage.usage);
    // Cost-reduction pass (2026-08-12, see ChatMessage.apiContentKind's own comment): the user
    // message this reply is answering — always the current last element, guaranteed by the same
    // append-only/isSessionBusy invariant landReplyOntoFreshSession's own comment already relies
    // on — has done its hidden instruction's one job the moment this reply lands. Clearing
    // apiContent here (not at click time) means the live request that's *about* to be answered
    // still went out with the full instruction; only future, unrelated resends of this message
    // stop carrying it.
    const answeredMessage = fresh.messages[fresh.messages.length - 1];
    if (answeredMessage?.apiContentKind) {
      answeredMessage.apiContent = undefined;
    }
    fresh.messages.push(assistantMessage);
    fresh.updatedAt = Date.now();
    saveSession(fresh);
    // Cleared *before* the render below, not left to sendUserMessage's own finally block —
    // renderIfStillOnScreen triggers a full re-render synchronously, which happens before
    // finally ever runs. If this entry were still present when that fresh render's own busy
    // check (applySendBtnPendingState) ran, it would see this session as still pending and put
    // the brand-new Send button right back into its grey/red "Thinking..." pending state — stuck
    // there until something else (switching chats and back) forced another render after finally
    // had actually cleared it. This was a real, confirmed bug, not a hypothetical.
    pendingRequests.delete(session.id);
    renderIfStillOnScreen(fresh.id);
  }

  // Regenerate's counterpart to landReplyOntoFreshSession above — replaces the message at a
  // fixed index instead of appending. Safe under the same "nothing else could have mutated the
  // messages array while this was pending" guarantee: sendUserMessage/regenerateReply both
  // check isSessionBusy before starting, and deleteMessagePair now refuses to run while busy
  // too (see that function's own comment) specifically so this index can't have shifted out
  // from under a still-pending regenerate.
  function landRegeneratedReplyOntoFreshSession(
    assistantIndex: number,
    assistantMessage: ChatMessage,
    usedProviderId: string | undefined
  ): void {
    // See landReplyOntoFreshSession's identical line for why this is cleared unconditionally,
    // up front, rather than only on the success path below.
    streamingReplyText.delete(session.id);
    const fresh = getSession(session.id);
    if (!fresh) {
      showError(s.sessionSwitcher.chatNoLongerExists);
      return;
    }
    // Multi-window staleness guard (2026-08-11): unlike landReplyOntoFreshSession's plain
    // append below, this replaces a message *at a fixed index* — fetching fresh only protects
    // against overwriting messages added elsewhere, not against that index having shifted or no
    // longer meaning what it did when regenerate was clicked (e.g. another window deleted or
    // regenerated around this same message while this request was in flight). `session.updatedAt`
    // still reflects the moment this window last rendered — unlike `session` itself, nothing
    // local mutates it between then and here — so comparing it against the fresh copy's is a
    // reliable "did anything else touch this chat since I started" check. Refuses to save
    // (the regenerated reply is discarded) rather than guessing at where it should now land.
    if (fresh.updatedAt !== session.updatedAt) {
      showError(s.chat.staleChatRegenerating);
      return;
    }
    applyUsageToFreshSession(fresh, assistantMessage.usage);
    // Same reasoning as sendUserMessage's own lastUsedProviderId update — a regenerate is a
    // real API call too, so if it ran under a provider different from the last one actually
    // used, that now counts as "last actually used" for the next providerChanges comparison.
    // Applied here (once landing is known to happen) rather than pre-emptively before the
    // request started, to avoid a second fetch-fresh-and-save just for this one field; skipped
    // entirely on cancel/generic-error, an acceptable minor gap since those don't represent a
    // completed exchange under the new provider either way.
    if (usedProviderId) {
      fresh.lastUsedProviderId = usedProviderId;
    }
    fresh.messages[assistantIndex] = assistantMessage;
    fresh.updatedAt = Date.now();
    saveSession(fresh);
    // See landReplyOntoFreshSession's identical line for why this has to happen here, before
    // the render, rather than being left to regenerateReply's own finally block.
    pendingRequests.delete(session.id);
    renderIfStillOnScreen(fresh.id);
  }

  function wireCollapseButtons(): void {
    messageListEl
      .querySelectorAll<HTMLButtonElement>('[data-action="toggle-collapse"]')
      .forEach((btn) => {
        btn.addEventListener("click", () => {
          const index = Number(btn.dataset.index);
          const key = `${session.id}:${index}`;
          if (collapsedMessageKeys.has(key)) {
            collapsedMessageKeys.delete(key);
          } else {
            collapsedMessageKeys.add(key);
          }
          renderMessages();
          scrollToMessagePair(index);
        });
      });
  }

  // Collapsing/expanding a reply changes its own height a lot (full text <-> 2 lines), which
  // shifts everything below it — renderMessages()'s default "preserve the old scrollTop" isn't
  // enough here, since the same pixel offset now points at different content. Anchoring to the
  // user message this reply answered (falling back to the reply itself if that lookup fails)
  // keeps the pair the user just interacted with in view instead of drifting to an unrelated
  // spot in the list.
  function scrollToMessagePair(assistantIndex: number): void {
    const userIndex = assistantIndex - 1;
    const anchorIndex = session.messages[userIndex]?.role === "user" ? userIndex : assistantIndex;
    messageListEl
      .querySelector<HTMLElement>(`[data-message-index="${anchorIndex}"]`)
      ?.scrollIntoView({ block: "start" });
  }

  function wireQuoteCollapseButtons(): void {
    messageListEl
      .querySelectorAll<HTMLButtonElement>('[data-action="toggle-quote"]')
      .forEach((btn) => {
        btn.addEventListener("click", () => {
          const key = btn.dataset.key as string;
          if (expandedQuoteKeys.has(key)) {
            expandedQuoteKeys.delete(key);
          } else {
            expandedQuoteKeys.add(key);
          }
          renderMessages();
        });
      });
  }

  // Same open/close mechanics as providerForm.ts's wireInfoIcons: click toggles an .is-open
  // class that a plain CSS rule uses to show the popup, and a document-level listener (added
  // only while open) closes it on any click outside the wrap. Confirming/cancelling both just
  // close it again — confirming additionally deletes first.
  function wireDeleteButtons(): void {
    messageListEl.querySelectorAll<HTMLElement>(".delete-confirm-wrap").forEach((wrap) => {
      const toggleBtn = wrap.querySelector(
        '[data-action="toggle-delete-confirm"]'
      ) as HTMLButtonElement;

      function closeOnOutsideClick(e: MouseEvent): void {
        if (!wrap.contains(e.target as Node)) {
          wrap.classList.remove("is-open");
          document.removeEventListener("click", closeOnOutsideClick);
        }
      }

      toggleBtn.addEventListener("click", (e) => {
        // Otherwise this same click immediately reaches the document listener added just below
        // and closes the popup it was meant to open.
        e.stopPropagation();
        const isOpen = wrap.classList.toggle("is-open");
        if (isOpen) {
          document.addEventListener("click", closeOnOutsideClick);
        } else {
          document.removeEventListener("click", closeOnOutsideClick);
        }
      });

      wrap.querySelector('[data-action="cancel-delete-pair"]')?.addEventListener("click", () => {
        wrap.classList.remove("is-open");
        document.removeEventListener("click", closeOnOutsideClick);
      });

      const confirmBtn = wrap.querySelector(
        '[data-action="confirm-delete-pair"]'
      ) as HTMLButtonElement | null;
      confirmBtn?.addEventListener("click", () => {
        document.removeEventListener("click", closeOnOutsideClick);
        deleteMessagePair(Number(confirmBtn.dataset.index));
      });
    });
  }

  function wireRegenerateButtons(): void {
    messageListEl
      .querySelectorAll<HTMLButtonElement>('[data-action="regenerate"]')
      .forEach((btn) => {
        btn.addEventListener("click", () => enterPendingRegenerateMode(Number(btn.dataset.index)));
      });
  }

  // Puts the compose box into "waiting for optional regenerate feedback" mode instead of firing
  // the request immediately — each regenerate can need something different (less flowery, redo
  // the pacing, keep the dialogue but cut the exposition), so a single canned "try again" note
  // isn't enough; letting the author type what they actually mean into the same box they already
  // use for everything else (rather than a small fixed popup) is what makes that flexible. Clears
  // and refocuses the box, discarding whatever draft was there — same reasoning as sendCritique's
  // clearInputBox: false does NOT apply here, since this box is what the author is about to type
  // the regenerate feedback into, not a draft for something else.
  function enterPendingRegenerateMode(index: number): void {
    if (isSessionBusy(session.id)) {
      showError(s.chat.busyRegenerateError);
      return;
    }
    pendingRegenerateIndex = index;
    input.value = "";
    input.placeholder = s.chat.regenerateFeedbackPlaceholder;
    regenerateBannerEl.style.display = "";
    sendBtn.textContent = s.chat.regenerateLabel;
    renderMessages();
    input.focus();
  }

  // Backs out of pending-regenerate mode without sending anything — the banner's own × button,
  // and Escape from the compose box (see the keydown handler below). Only relevant before submit;
  // once regenerateReply's request actually starts, pendingRegenerateIndex stays set until it
  // resolves (see that variable's own comment) and this function is no longer reachable via the
  // banner (hidden) or Escape (guarded by isSessionBusy at the call site).
  function cancelPendingRegenerate(): void {
    if (pendingRegenerateIndex === null) return;
    pendingRegenerateIndex = null;
    input.value = "";
    input.placeholder = s.chat.typeMessagePlaceholder;
    regenerateBannerEl.style.display = "none";
    sendBtn.textContent = s.chat.send;
    renderMessages();
  }

  // Deletes an assistant reply along with the user message it answered. Messages strictly
  // alternate user, assistant, user, assistant... (see sendCurrentMessage below, which always
  // pushes a user turn before ever requesting a reply), so the message this reply answered is
  // always the one immediately before it.
  function deleteMessagePair(assistantIndex: number): void {
    // Blocked while a request is pending for this session (same isSessionBusy check as
    // Send/Regenerate) — Regenerate replaces a message at a fixed array index once its reply
    // lands, and a concurrent delete here would shift every index after the removed pair,
    // landing that reply on the wrong message.
    if (isSessionBusy(session.id)) {
      showError(s.chat.busyDeleteError);
      return;
    }
    // Multi-window staleness guard (2026-08-11) — same reasoning as sendUserMessage's own: this
    // removes messages *by index* from `session.messages`, so if another window changed this
    // chat since this one last rendered, those indices may no longer point at what the author
    // actually clicked delete on. Refuse rather than delete the wrong thing. Checked *before*
    // cancelPendingRegenerate below (fixed — this used to run first and unconditionally, so a
    // delete rejected here for staleness still silently discarded any in-progress regenerate
    // feedback/banner even though nothing was actually deleted).
    const freshBeforeDelete = getSession(session.id);
    if (!freshBeforeDelete) {
      showError(s.sessionSwitcher.chatNoLongerExists);
      return;
    }
    if (freshBeforeDelete.updatedAt !== session.updatedAt) {
      showError(s.chat.staleChatGeneric);
      return;
    }
    // Any pending-regenerate mode (waiting on typed feedback, not yet submitted — see
    // pendingRegenerateIndex's own comment) is now stale the moment any pair is deleted, since
    // Regenerate only ever targets "the last message" and a delete can change which message that
    // is. No-op if nothing was pending. A regenerate *request already in flight* is a different
    // state (isSessionBusy true) already refused above, so this can't fire mid-request.
    cancelPendingRegenerate();
    const userIndex = assistantIndex - 1;
    const indicesToRemove = new Set([assistantIndex]);
    if (session.messages[userIndex]?.role === "user") {
      indicesToRemove.add(userIndex);
    }
    session.messages = session.messages.filter((_, i) => !indicesToRemove.has(i));
    session.updatedAt = Date.now();
    saveSession(session);

    // collapsedMessageKeys/expandedQuoteKeys are keyed by message index, and every index after
    // the removed pair just shifted — clearing this session's entries in both avoids a stale
    // key silently collapsing/expanding the wrong message after the shift.
    for (const key of collapsedMessageKeys) {
      if (key.startsWith(`${session.id}:`)) collapsedMessageKeys.delete(key);
    }
    for (const key of expandedQuoteKeys) {
      if (key.startsWith(`${session.id}:`)) expandedQuoteKeys.delete(key);
    }
    renderMessages();
  }

  // Blank spinner before the first streamed chunk arrives for this session (mirrors the
  // original pre-streaming "Thinking..." wait exactly), or the actual growing reply text once
  // streamingReplyText has something accumulated — shared by showTypingIndicator (full
  // render/restore) and updateStreamingBubble (per-chunk patch, below) so both stay visually
  // identical regardless of which one last touched the DOM. Rendered through renderMarkdownLite
  // the same way a landed message is (recomputed against the *whole* string so far on every
  // call, not appended incrementally) — cheap for a short/medium string, and means formatting
  // (bold/italic/quote lines) never visibly reflows the instant the reply lands, the way a raw-
  // text-then-formatted-at-the-end approach would. The trailing cursor span is removed the
  // moment this bubble is replaced by the real landed message (a full renderMessages() rebuild),
  // so it never needs explicit cleanup of its own.
  function pendingReplyBubbleHtml(accumulated: string): string {
    return accumulated
      ? `<div class="message-bubble"><div class="message-bubble-text">${renderMarkdownLite(accumulated)}<span class="streaming-cursor"></span></div></div>`
      : '<div class="message-bubble typing-bubble"><span class="spinner"></span></div>';
  }

  // Shown only while a reply is in flight — not part of session.messages, so it never gets
  // persisted or exported. Appended straight to the DOM (not via renderMessages) so it doesn't
  // require session state; a successful reply's renderChat() rebuilds the whole list anyway
  // (which removes this along with everything else), so hideTypingIndicator() below is only
  // strictly needed for the error path, but is called unconditionally for both since it's a
  // harmless no-op against an already-replaced/detached tree. data-pending-reply is what
  // updateStreamingBubble (below) re-queries on every chunk, rather than caching a direct
  // reference — this exact element gets wiped by any unrelated renderMessages() rebuild that
  // happens to run mid-stream (collapsing another reply, toggling EN/CH, the staleness poll's
  // reload), and only this function (called again by that same rebuild's own isSessionBusy
  // check) recreates it.
  function showTypingIndicator(): void {
    const indicator = document.createElement("div");
    indicator.className = "message message-assistant typing-indicator";
    indicator.dataset.pendingReply = session.id;
    indicator.innerHTML = pendingReplyBubbleHtml(streamingReplyText.get(session.id) ?? "");
    // Second way to stop a pending request, alongside the Stop button above Send — per explicit
    // request. cursor: crosshair (taskpane.css) is the visual cue on hover; the click itself
    // calls the exact same stopPendingRequest() the Stop button does, so there's one shared
    // cancellation path regardless of which control triggered it. Only meaningful while the
    // blank-spinner state is showing (.typing-bubble) — once real text is streaming in, the
    // bubble is a plain .message-bubble with nothing to click for Stop; Stop/the Send button
    // itself remains the way to cancel from that point on.
    indicator.querySelector(".typing-bubble")?.addEventListener("click", stopPendingRequest);
    messageListEl.appendChild(indicator);
    messageListEl.scrollTop = messageListEl.scrollHeight;
  }

  function hideTypingIndicator(): void {
    messageListEl.querySelector(".typing-indicator")?.remove();
  }

  // Called on every onChunk callback from the adapter (sendUserMessage/regenerateReply) —
  // patches the live indicator bubble in place rather than re-rendering the whole message list
  // on every single chunk, which could fire many times a second. Re-queries the DOM by
  // data-pending-reply on every call, same reasoning as showTypingIndicator's own comment above
  // — never caches a reference across calls. Falls back to creating a fresh indicator if one
  // isn't currently present (the render that would normally hold it may have been replaced by
  // something else entirely, e.g. navigating to a different screen and back mid-stream).
  function updateStreamingBubble(accumulated: string): void {
    const indicator = messageListEl.querySelector<HTMLElement>(
      `.typing-indicator[data-pending-reply="${session.id}"]`
    );
    if (!indicator) {
      showTypingIndicator();
      return;
    }
    indicator.innerHTML = pendingReplyBubbleHtml(accumulated);
    indicator.querySelector(".typing-bubble")?.addEventListener("click", stopPendingRequest);
    messageListEl.scrollTop = messageListEl.scrollHeight;
  }

  // Shared setup for both sendUserMessage and regenerateReply — a fresh AbortController tracked
  // in pendingRequests (see that map's own comment: one pending request per session, strictly
  // serialized — Send and Regenerate share this one slot), the streaming accumulator reset to ""
  // (not deleted — showTypingIndicator reads this to decide blank-spinner vs. live-text, and a
  // request that's just starting genuinely has no text yet), the Send button's pending state, and
  // the typing indicator itself. Only this setup step is shared — the try/catch/finally around
  // the actual sendMessage() call stays separate in each caller, since the two genuinely diverge
  // there (different landing calls, different error strings, regenerate's extra cleanup).
  function beginPendingRequest(): { controller: AbortController; startedAt: number } {
    const controller = new AbortController();
    const startedAt = Date.now();
    pendingRequests.set(session.id, { controller, startedAt });
    streamingReplyText.set(session.id, "");
    applySendBtnPendingState(0, session.id, controller);
    showTypingIndicator();
    return { controller, startedAt };
  }

  // Shared onChunk callback for both sendUserMessage's and regenerateReply's sendMessage() calls.
  function accumulateStreamingChunk(delta: string): void {
    const accumulated = (streamingReplyText.get(session.id) ?? "") + delta;
    streamingReplyText.set(session.id, accumulated);
    updateStreamingBubble(accumulated);
  }

  // Shared by both functions' catch blocks — whether this exact session is still the one showing
  // on screen (as opposed to the author having navigated elsewhere while the request was in
  // flight), i.e. whether there's anywhere to actually show an error/cancellation notice.
  function isChatScreenShowingSession(): boolean {
    return Boolean(
      container.querySelector(".chat-screen") &&
      contentMode?.kind === "session" &&
      contentMode.sessionId === session.id
    );
  }

  renderMessages(true);

  if (!provider) {
    showError(s.chat.providerGoneError);
    input.disabled = true;
    sendBtn.disabled = true;
    return;
  }

  function renderChip(): void {
    if (!pendingSelection) {
      chipContainer.innerHTML = "";
      return;
    }
    const preview =
      pendingSelection.length > 80 ? `${pendingSelection.slice(0, 80)}…` : pendingSelection;
    const toggleHint = selectionIncluded ? s.chat.excludeHint : s.chat.includeHint;
    const critiqueDisabled = critiqueUsedSelections.has(`${session.id}:${pendingSelection}`);
    chipContainer.innerHTML = `
      <div class="selection-chip${selectionIncluded ? "" : " excluded"}" id="selection-chip" title="${escapeAttr(toggleHint)}">
        <span class="selection-chip-text">${escapeHtml(preview)}</span>
        <button type="button" class="selection-chip-critique" id="selection-chip-critique" ${critiqueDisabled ? "disabled" : ""} title="${critiqueDisabled ? s.chat.alreadyCritiqued : ""}">${s.chat.critiqueThis}</button>
        <span class="selection-chip-state">${selectionIncluded ? s.chat.attached : s.chat.excluded}</span>
        <button type="button" class="selection-chip-remove" aria-label="${s.chat.removeSelectionAria}">&times;</button>
      </div>
    `;
    chipContainer.querySelector("#selection-chip")?.addEventListener("click", () => {
      selectionIncluded = !selectionIncluded;
      renderChip();
    });
    chipContainer.querySelector("#selection-chip-critique")?.addEventListener("click", (e) => {
      // Same stopPropagation reasoning as the remove button below — otherwise this click would
      // also reach the chip's own toggle handler. Sends immediately (see sendCritique) rather
      // than filling the compose box for the author to review/edit/send — the critique rubric
      // is a fixed, hidden instruction, not something meant to be seen or tweaked per use.
      // No `if (critiqueDisabled) return` guard needed here — a disabled button never dispatches
      // click events in the first place.
      e.stopPropagation();
      void sendCritique();
    });
    chipContainer.querySelector(".selection-chip-remove")?.addEventListener("click", (e) => {
      // Stops the click from also reaching the chip's own toggle handler above (that handler
      // is on the parent, so without this the click would both remove AND re-toggle it).
      e.stopPropagation();
      pendingSelection = null;
      selectionIncluded = true;
      renderChip();
    });
  }

  function handleSelectionChanged(): void {
    getSelectedText()
      .then((text) => {
        const trimmed = text.trim() || null;
        if (trimmed !== pendingSelection) {
          selectionIncluded = true;
        }
        pendingSelection = trimmed;
        renderChip();
      })
      .catch(() => {
        // Background tracking — a read failure here isn't worth surfacing as an error.
      });
  }

  // Standalone (non-Word) entry point has no document to select from, and never loads office.js
  // at all — skip this wiring there entirely rather than let it throw. renderChip()'s own
  // `if (!pendingSelection) return` (unchanged) means the selection chip simply never appears
  // when this is skipped, which is exactly standalone's "no attach text" design — no separate
  // standalone-specific branch needed anywhere else in this file.
  if (isWordAvailable()) {
    Office.context.document.addHandlerAsync(
      Office.EventType.DocumentSelectionChanged,
      handleSelectionChanged
    );
    activeSelectionHandlerCleanup = () => {
      Office.context.document.removeHandlerAsync(Office.EventType.DocumentSelectionChanged);
    };
    handleSelectionChanged();
  }

  // Shared tail for every path that sends a new user turn (typed message, typed+attached
  // selection, raw-pasted selection, and the critique quick action below) — builds the
  // ChatMessage, saves/renders it, then makes the actual API call and handles the
  // success/error/finally bookkeeping. `content` is what's shown and saved; `apiContent`
  // (if given) is what's actually sent to the provider instead — see NOTE_DETECTION_INSTRUCTION
  // and ChatMessage.apiContent for why these can differ. `attachedSelection` controls whether
  // the selection chip gets marked excluded afterward (same "leave it there, just toggled off"
  // behavior either way — see the comment at that call site). `clearInputBox` is false only for
  // the critique quick action, which sends independently of whatever the author has typed (but
  // not yet sent) in the compose box — that draft shouldn't be silently wiped by an unrelated
  // one-click action.
  async function sendUserMessage(
    content: string,
    apiContent: string | undefined,
    attachedSelection: boolean,
    clearInputBox: boolean = true,
    apiContentKind?: ChatMessage["apiContentKind"]
  ): Promise<void> {
    // Defensive — Send/Critique should already be disabled while busy (see the
    // isSessionBusy check in renderActiveSession and this function's own finally block below),
    // so this shouldn't normally be reachable. Guards against it anyway rather than starting a
    // second concurrent request the Stop button/pendingRequests map isn't designed to track.
    if (isSessionBusy(session.id)) return;

    errorEl.style.display = "none";

    // Multi-window staleness guard (2026-08-11): this pushes the new user message onto `session`
    // directly and saves it — unlike landReplyOntoFreshSession's later fetch-fresh-and-append,
    // this is the *first* save in the send flow and still operates on whatever this window
    // rendered last. If another window changed this chat since then, saving now would silently
    // overwrite that change. `getSession` fetches the real current record; if its `updatedAt`
    // doesn't match what this window expects, something changed elsewhere — refuse to send
    // rather than guess, and tell the author to reload. If they match, `session` here is
    // guaranteed content-identical to `fresh` (nothing bumps `updatedAt` without a save, and
    // nothing else saves this exact object between renders), so there's no need to actually
    // switch to using `fresh` below — this check alone is enough.
    const freshBeforeSend = getSession(session.id);
    if (!freshBeforeSend) {
      showError(s.sessionSwitcher.chatNoLongerExists);
      return;
    }
    if (freshBeforeSend.updatedAt !== session.updatedAt) {
      showError(s.chat.staleChatGeneric);
      return;
    }

    const userMessage: ChatMessage = {
      role: "user",
      content,
      timestamp: Date.now(),
      apiContent,
      apiContentKind,
    };
    // Logs a providerChanges entry here — the moment a message is actually about to be sent
    // under a provider different from the one last actually used — rather than at "Edit
    // session" save time (see that handler's comment for why: switching back and forth without
    // ever sending anything would otherwise log markers for a round trip nothing was sent
    // through). lastUsedProviderId undefined means this session has never sent a message yet;
    // that first send is deliberately never itself treated as a "change" — there's nothing to
    // compare it against. changedAt uses this exact message's own timestamp, so the divider
    // (see ChatSession.providerChanges) positions itself immediately before it.
    if (
      provider &&
      session.lastUsedProviderId !== undefined &&
      session.lastUsedProviderId !== session.providerId
    ) {
      session.providerChanges = [
        ...(session.providerChanges ?? []),
        { providerName: provider.name, changedAt: userMessage.timestamp },
      ];
    }
    if (provider) {
      session.lastUsedProviderId = session.providerId;
    }
    session.messages.push(userMessage);
    session.updatedAt = Date.now();
    saveSession(session);
    renderMessages(true);
    if (clearInputBox) {
      input.value = "";
    }
    if (attachedSelection) {
      // Mark excluded rather than clearing — Word's DocumentSelectionChanged can refire with the
      // same still-selected text (e.g. refocusing the pane), and since pendingSelection is kept
      // (not nulled), handleSelectionChanged's "did the text actually change" check below won't
      // mistake that refire for a new selection and silently re-attach it to the next message.
      selectionIncluded = false;
    }
    renderChip();

    const { controller, startedAt } = beginPendingRequest();

    // provider's own fields are only its defaults — the session can override several of them
    // (Edit session), so both the truncation budget below and the actual request have to use
    // the session's effective values, or a session-level change would show in the header but
    // silently not affect what's actually sent/budgeted.
    const effectiveConfig = effectiveProviderConfig(session, provider);
    const { messages: historyToSend } = getTruncatedHistory(session, effectiveConfig);

    try {
      const outgoingMessages = historyToSend.map((m) => ({
        role: m.role,
        content: m.apiContent ?? m.content,
      }));
      logDebugRequest(effectiveConfig, outgoingMessages);
      const reply = await sendMessage(
        effectiveConfig,
        outgoingMessages,
        controller.signal,
        accumulateStreamingChunk
      );

      landReplyOntoFreshSession({
        role: "assistant",
        content: reply.text,
        timestamp: Date.now(),
        usage: reply.usage,
        responseTimeSeconds: Math.round((Date.now() - startedAt) / 100) / 10,
      });
    } catch (err) {
      // The stream (if it ever started) is genuinely over — clear its accumulated text here too,
      // not just on the success path (landReplyOntoFreshSession's own delete only covers that
      // one), so a cancelled/failed request never leaves stale text behind for some later render
      // to mistakenly redraw as still in progress.
      streamingReplyText.delete(session.id);
      const stillOnScreen = isChatScreenShowingSession();
      if (err instanceof DOMException && err.name === "AbortError") {
        // Deliberate cancellation (Stop button / typing-bubble click) — a transient notice, not
        // a permanent bubble.
        if (stillOnScreen) showError(s.chat.replyCancelled);
      } else if (stillOnScreen) {
        // A failed API call shows a clear inline error, never crashes the pane.
        showError(isProviderApiError(err) ? err.message : s.chat.sendError);
      }
      // If the user has navigated elsewhere, there's nowhere to show this — nothing was saved
      // for a failed send, so nothing is silently lost, just not surfaced until they retry.
    } finally {
      pendingRequests.delete(session.id);
      // No-op if renderChat() above already replaced this closure's DOM tree (the success path,
      // which renders via landReplyOntoFreshSession) — only actually removes/re-enables anything
      // on the cancelled/error paths, where nothing else re-rendered.
      hideTypingIndicator();
      input.disabled = false;
      sendBtn.type = "submit";
      sendBtn.disabled = false;
      sendBtn.textContent = s.chat.send;
      sendBtn.classList.remove("is-thinking", "is-stop-thinking");
      input.focus();
    }
  }

  async function sendCurrentMessage(): Promise<void> {
    const typed = input.value.trim();
    // The chip's text (if present and currently toggled on) is combined with what you typed
    // only at send time — it never has to be manually formatted or read back out of the input
    // box. An excluded chip is left untouched below rather than cleared, so it's still there
    // to toggle back on for a later message without re-highlighting in Word.
    const attachSelection = pendingSelection !== null && selectionIncluded;
    if (!typed && !attachSelection) {
      showError(s.chat.typeOrSelectFirst);
      return;
    }

    // Nothing typed: send the attached passage completely raw — no label, no quoting, no added
    // wording of any kind, matching the author's own habit of pasting a passage (sometimes with
    // notes/questions written directly into it) straight into a web AI chat and hitting send.
    // The label/quote treatment below only kicks in once there's *also* a typed instruction,
    // where it's needed to distinguish "the passage" from "what to do with it".
    const quotedSelection = attachSelection ? toQuoteBlock(pendingSelection as string) : "";
    const selectionLabel = s.chat.selectedTextLabel;
    const content = !typed
      ? (pendingSelection as string)
      : attachSelection
        ? `${selectionLabel}\n${quotedSelection}\n\n${typed}`
        : typed;
    // Same passage, shown to the provider only — see NOTE_DETECTION_INSTRUCTION and
    // ChatMessage.apiContent. Only differs from `content` in the "label + typed instruction"
    // case: the raw-passage-only send above needs no help (there's no label there suppressing
    // the model's read of embedded notes in the first place — that's what caused the original
    // bug), and a plain typed message with nothing attached has no passage to watch for notes in.
    // No language directive appended here (removed 2026-08-12, see chatPrompts.ts's top comment)
    // — the message is sent as-is and the model decides what language to reply in.
    const apiContent =
      typed && attachSelection
        ? `${selectionLabel}\n${quotedSelection}\n\n${NOTE_DETECTION_INSTRUCTION}\n\n${typed}`
        : undefined;

    await sendUserMessage(
      content,
      apiContent,
      attachSelection,
      true,
      apiContent ? "noteDetection" : undefined
    );
  }

  // The critique quick action (see effectiveCritiquePrompt/CRITIQUE_CHAPTER_PROMPT in
  // chatPrompts.ts and the chip button above): sends immediately on click rather than filling
  // the compose box and waiting for the author to hit Send — same immediacy as
  // pasting-and-sending a raw selection. The visible bubble shows only a label ("Critique text
  // from the document:") and the quoted passage, matching the look of an attached-selection
  // message; the actual critique rubric is never shown or saved, only sent via apiContent — same
  // content/apiContent split used for NOTE_DETECTION_INSTRUCTION above, just carrying the whole
  // instruction instead of one line appended to a visible typed one. Its exact text is whatever
  // the Settings screen currently resolves to — the app's built-in rubric, or the author's own
  // saved override — not always the same literal constant.
  // `clearInputBox: false` on the sendUserMessage call below is deliberate: this bypasses the
  // compose box entirely, so it must not clear a draft the author was in the middle of typing
  // there.
  async function sendCritique(): Promise<void> {
    if (!pendingSelection) return;
    const selection = pendingSelection;
    // Set immediately on click, not after the reply comes back — greys the button out right
    // away (renderChip), matching "once clicked" rather than "once answered". sendUserMessage
    // below calls renderChip() itself (via the attachedSelection path), so this doesn't need
    // its own render call here.
    critiqueUsedSelections.add(`${session.id}:${selection}`);
    const quotedSelection = toQuoteBlock(selection);
    const label = s.chat.critiqueTextLabel;
    const content = `${label}\n${quotedSelection}`;
    // No language directive appended here (removed 2026-08-12, see chatPrompts.ts's top
    // comment) — the message is sent as-is and the model decides what language to reply in.
    const apiContent = `${label}\n${quotedSelection}\n\n${effectiveCritiquePrompt()}`;

    await sendUserMessage(content, apiContent, true, false, "critique");
  }

  // Re-asks for this one reply using the exact context it was originally generated from — not
  // a new turn, so nothing is appended to session.messages; the reply at assistantIndex is
  // replaced in place with the fresh one. Point 2 from a real author's workflow analysis: the
  // author picks between differently-toned takes on the same request rather than always wanting
  // the first answer kept. `feedback`, if given, is the author's own words on what should be
  // different this time (typed into the compose box in pending-regenerate mode — see
  // enterPendingRegenerateMode/submitRegenerate below) — regenerateInstruction() decides how
  // that gets turned into the synthetic final turn sent to the model.
  async function regenerateReply(
    assistantIndex: number,
    feedback: string | undefined
  ): Promise<void> {
    // Same guard/reasoning as sendUserMessage — regenerate shares the same one-pending-request-
    // per-session slot (isSessionBusy), so Send and Regenerate can't run concurrently either.
    // Defensive here (enterPendingRegenerateMode already checked this before pendingRegenerateIndex
    // was ever set) rather than reachable in normal use.
    if (isSessionBusy(session.id)) return;

    errorEl.style.display = "none";

    // Includes the reply being regenerated (unlike a normal send's history) so the model can
    // see what it said last time — regenerateInstruction()'s note below, appended only to the
    // outgoing API call and never stored, is what tells it that answer was rejected/needs a
    // specific change, not just re-asked blind.
    const historyIncludingReply: ChatSession = {
      ...session,
      messages: session.messages.slice(0, assistantIndex + 1),
    };
    // Same effective (session-overrides-over-provider) config used for both the truncation
    // budget and the actual request — see effectiveProviderConfig's own comment.
    const effectiveConfig = effectiveProviderConfig(session, provider);
    const { messages: historyToSend } = getTruncatedHistory(historyIncludingReply, effectiveConfig);

    // Regenerate has no in-flight visual of its own beyond the .is-regenerate-target outline
    // (set when pending-regenerate mode was *entered*, before this function even runs) — the
    // shared bottom-of-list live bubble is otherwise only shown if some unrelated render happens
    // to run while this is pending. beginPendingRequest's own showTypingIndicator call handles it
    // here too, reusing the exact same mechanism sendUserMessage uses, so a regenerate reliably
    // streams into the same place a normal send does rather than replacing the outlined original
    // reply in place.
    const { controller, startedAt } = beginPendingRequest();

    try {
      const outgoingMessages = [
        ...historyToSend.map((m) => ({ role: m.role, content: m.apiContent ?? m.content })),
        {
          role: "user" as const,
          content: regenerateInstruction(feedback),
        },
      ];
      logDebugRequest(effectiveConfig, outgoingMessages);
      const reply = await sendMessage(
        effectiveConfig,
        outgoingMessages,
        controller.signal,
        accumulateStreamingChunk
      );

      // Only when real feedback was given — a blank regenerate has no specific direction worth
      // persisting (see regenerateAcceptedApiContent's own comment). Without this, the
      // correction reached the model for this one call only; every later message in the same
      // chat would otherwise have no memory a redirect happened at all.
      const trimmedFeedback = feedback?.trim();
      landRegeneratedReplyOntoFreshSession(
        assistantIndex,
        {
          role: "assistant",
          content: reply.text,
          timestamp: Date.now(),
          usage: reply.usage,
          responseTimeSeconds: Math.round((Date.now() - startedAt) / 100) / 10,
          apiContent: trimmedFeedback
            ? regenerateAcceptedApiContent(trimmedFeedback, reply.text)
            : undefined,
        },
        provider?.id
      );
    } catch (err) {
      // See sendUserMessage's identical line — the stream (if any) is over either way, clear it
      // here too so nothing stale lingers for a later render to misread as still in progress.
      streamingReplyText.delete(session.id);
      const stillOnScreen = isChatScreenShowingSession();
      if (err instanceof DOMException && err.name === "AbortError") {
        // Nothing to land — the original reply is untouched, exactly as if regenerate was never
        // clicked (aside from whatever the provider may have already billed for the attempt).
        if (stillOnScreen) showError(s.chat.regenerationCancelled);
      } else if (stillOnScreen) {
        showError(isProviderApiError(err) ? err.message : s.chat.regenerateError);
      }
    } finally {
      pendingRequests.delete(session.id);
      pendingRegenerateIndex = null;
      // No-op if renderChat() above already replaced this closure's DOM tree (the success path)
      // — same reasoning as sendUserMessage's own hideTypingIndicator call; only actually removes
      // anything on the cancelled/error paths, where nothing else re-rendered.
      hideTypingIndicator();
      input.disabled = false;
      input.placeholder = s.chat.typeMessagePlaceholder;
      sendBtn.type = "submit";
      sendBtn.disabled = false;
      sendBtn.textContent = s.chat.send;
      sendBtn.classList.remove("is-thinking", "is-stop-thinking");
      regenerateBannerEl.style.display = "none";
      // No-op if renderChat() above already replaced this closure's DOM tree (the success path,
      // via landRegeneratedReplyOntoFreshSession's renderIfStillOnScreen) — messageListEl would be
      // a detached node by then, so this guard avoids re-rendering into it. Only actually needed
      // on the cancelled/error paths, to drop the .is-regenerate-target highlight (renderMessages
      // reads pendingRegenerateIndex, now null) — the success path doesn't need it since the fresh
      // render already started with a clean pendingRegenerateIndex of its own.
      if (messageListEl.isConnected) renderMessages();
    }
  }

  // Submits whatever's in the compose box (blank is fine — see regenerateInstruction) as feedback
  // for whichever reply pendingRegenerateIndex is currently targeting. Mirrors sendCurrentMessage's
  // role for a normal Send; form submit/Enter dispatch to whichever of the two applies (below).
  async function submitRegenerate(): Promise<void> {
    if (pendingRegenerateIndex === null) return;
    const feedback = input.value.trim();
    input.value = "";
    await regenerateReply(pendingRegenerateIndex, feedback || undefined);
  }

  form.addEventListener("submit", (e) => {
    e.preventDefault();
    if (pendingRegenerateIndex !== null) {
      void submitRegenerate();
    } else {
      void sendCurrentMessage();
    }
  });

  // Enter submits (Send, or Regenerate while in pending-regenerate mode); Shift+Enter inserts a
  // newline, matching the usual web chat convention. Escape backs out of pending-regenerate mode
  // without sending anything — only while it's still waiting on optional feedback, not once the
  // request has actually started (isSessionBusy guards that, same as everywhere else pending-
  // regenerate mode is entered/exited).
  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (pendingRegenerateIndex !== null) {
        void submitRegenerate();
      } else {
        void sendCurrentMessage();
      }
    } else if (
      e.key === "Escape" &&
      pendingRegenerateIndex !== null &&
      !isSessionBusy(session.id)
    ) {
      e.preventDefault();
      cancelPendingRegenerate();
    }
  });
}
